// MarginPercentageChart.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { apiFetch } from '../../utils/api';
import { CURRENT_YEAR, CURRENT_QUARTER } from '../../constants';
import { Logger } from '../../utils/logger';

const logger = new Logger('MarginPercentageChart');

/**
 * A dedicated component for displaying margin percentage data with time frame toggles.
 * This component handles its own data fetching and state management.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.employee - Employee object containing employee_id
 * @param {boolean} props.hideTitle - Whether to hide the title
 * @param {boolean} props.compact - Whether to display in compact mode
 * @param {boolean} props.hideToggle - Whether to hide the time frame toggle
 * @param {string} props.timeFrame - Time frame to use (if controlled externally)
 */
const MarginPercentageChart = ({ 
  employee, 
  hideTitle = false, 
  compact = false,
  hideToggle = false,
  timeFrame: externalTimeFrame = null
}) => {
  // State for time frame toggle and data
  const [internalTimeFrame, setInternalTimeFrame] = useState('current_year');
  
  // Use external timeFrame if provided, otherwise use internal state
  const timeFrame = externalTimeFrame || internalTimeFrame;
  const [marginData, setMarginData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('Current Year Margin Percentage');
  
  // Format percentage for display
  const formatPercentage = (value) => {
    if (value === undefined || value === null) return 'N/A';
    return `${value.toFixed(1)}%`;
  };
  
  // Calculate average margin percentage
  const calculateAverageMargin = (data) => {
    if (!data || data.length === 0) return 0;
    
    // Filter out entries with null or zero percentages
    const validPercentages = data.filter(item => item.percentage !== null && item.percentage !== 0);
    
    if (validPercentages.length === 0) return 0;
    
    // Calculate average using only valid percentages
    return validPercentages.reduce((sum, item) => sum + item.percentage, 0) / validPercentages.length;
  };
  
  // Fetch margin percentage data based on time frame
  const fetchMarginData = useCallback(async () => {
    if (!employee?.employee_id) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      logger.debug(`Fetching financial impact data with time_frame=${timeFrame}`);
      
      // Make authenticated API call
      const response = await apiFetch(
        `employees/${employee.employee_id}/financial-impact?period_type=quarterly&time_frame=${timeFrame}`
      );
      const responseData = await response.json();

      // Extract margin percentage data
      const data = responseData?.margin_percentage_data?.quarterly || [];

      // If no data at all, surface the empty state rather than rendering zero bars
      if (data.length === 0) {
        setMarginData([]);
        const titleMap = {
          current_year: 'Current Year Margin Percentage',
          last_4_quarters: 'Last 4 Quarters Margin Percentage',
          last_year: 'Previous Year Margin Percentage',
        };
        setTitle(titleMap[timeFrame] || 'Margin Percentage');
        setIsLoading(false);
        return;
      }

      // Process data based on time frame
      let processedData = [];
      let chartTitle = '';

      switch (timeFrame) {
        case 'current_year':
          // Use the constant for current year
          const currentYear = CURRENT_YEAR;
          
          // Q1 → Q4 left to right (oldest to newest)
          const currentYearQuarters = [
            { period: `Q1 ${currentYear}`, percentage: 0 },
            { period: `Q2 ${currentYear}`, percentage: 0 },
            { period: `Q3 ${currentYear}`, percentage: 0 },
            { period: `Q4 ${currentYear}`, percentage: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(currentYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  currentYearQuarters[quarterIndex].percentage = item.percentage;
                }
              }
            }
          });

          processedData = currentYearQuarters;
          chartTitle = 'Current Year Margin Percentage';
          break;

        case 'last_4_quarters':
          const currentQuarter = CURRENT_QUARTER;
          const year = CURRENT_YEAR;

          const chronologicalQuarters = [];
          for (let i = 3; i >= 0; i--) {
            let q = currentQuarter - i;
            let y = year;
            if (q <= 0) { q += 4; y -= 1; }
            chronologicalQuarters.push({ period: `Q${q} ${y}`, percentage: 0 });
          }

          data.forEach(item => {
            const index = chronologicalQuarters.findIndex(q => q.period === item.period);
            if (index !== -1) chronologicalQuarters[index].percentage = item.percentage;
          });

          processedData = chronologicalQuarters;
          chartTitle = 'Last 4 Quarters Margin Percentage';
          break;

        case 'last_year':
          const lastYear = CURRENT_YEAR - 1;

          const lastYearQuarters = [
            { period: `Q1 ${lastYear}`, percentage: 0 },
            { period: `Q2 ${lastYear}`, percentage: 0 },
            { period: `Q3 ${lastYear}`, percentage: 0 },
            { period: `Q4 ${lastYear}`, percentage: 0 }
          ];

          data.forEach(item => {
            if (item.period.includes(lastYear.toString())) {
              const quarterMatch = item.period.match(/Q(\d)/);
              if (quarterMatch) {
                const quarterIndex = parseInt(quarterMatch[1]) - 1;
                if (quarterIndex >= 0 && quarterIndex < 4) {
                  lastYearQuarters[quarterIndex].percentage = item.percentage;
                }
              }
            }
          });

          processedData = lastYearQuarters;
          chartTitle = 'Previous Year Margin Percentage';
          break;
          
        default:
          processedData = data.slice(0, 4); // Limit to 4 items
          chartTitle = 'Margin Percentage';
      }
      
      setMarginData(processedData);
      setTitle(chartTitle);
      setIsLoading(false);
      
      logger.debug('Processed margin percentage data:', processedData);
    } catch (error) {
      logger.error('Error fetching financial impact data:', error);
      setError('Failed to load margin percentage data');
      setIsLoading(false);
    }
  }, [employee, timeFrame]);
  
  // Fetch data when employee or time frame changes
  useEffect(() => {
    fetchMarginData();
  }, [employee, timeFrame, fetchMarginData]);
  
  // Handle time frame change
  const handleTimeFrameChange = (event, newTimeFrame) => {
    if (newTimeFrame !== null) {
      setInternalTimeFrame(newTimeFrame);
    }
  };
  
  // Chart data
  const chartData = {
    labels: marginData.map(item => item.period),
    datasets: [
      {
        label: 'Margin Percentage',
        data: marginData.map(item => item.percentage),
        backgroundColor: '#FFCF44',
        borderRadius: 4
      }
    ]
  };
  
  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          },
          callback: function(value) {
            return `${value}%`;
          }
        }
      },
      x: {
        grid: {
          color: '#444',
          borderDash: [3, 3]
        },
        ticks: {
          color: '#aaa',
          font: {
            size: 12
          }
        }
      }
    },
    plugins: {
      tooltip: {
        backgroundColor: '#333',
        borderColor: '#555',
        borderWidth: 1,
        titleColor: '#fff',
        bodyColor: '#FFCF44',
        cornerRadius: 4,
        padding: 10,
        callbacks: {
          label: function(context) {
            return `Margin: ${context.raw.toFixed(1)}%`;
          }
        }
      },
      legend: {
        display: false
      }
    }
  };
  
  // Calculate average margin percentage
  const averageMargin = calculateAverageMargin(marginData);
  
  // Adjust chart options for compact mode
  if (compact) {
    chartOptions.scales.x.ticks.font.size = 10;
    chartOptions.scales.y.ticks.font.size = 10;
    chartOptions.plugins.tooltip.titleFont = { size: 10 };
    chartOptions.plugins.tooltip.bodyFont = { size: 10 };
    chartOptions.plugins.tooltip.padding = 6;
    chartData.datasets[0].borderRadius = 3;
  }

  return (
    <Box>
      {/* Toggle buttons - hidden in compact mode or when hideToggle is true */}
      {!compact && !hideToggle && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={timeFrame}
            exclusive
            onChange={handleTimeFrameChange}
            aria-label="time frame"
          >
            <ToggleButton value="current_year" aria-label="current_year">
              Current Year
            </ToggleButton>
            <ToggleButton value="last_4_quarters" aria-label="last_4_quarters">
              Last 4 Quarters
            </ToggleButton>
            <ToggleButton value="last_year" aria-label="last_year">
              Last Year
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      {/* Summary Card - simplified in compact mode */}
      {!compact ? (
        <Box sx={{ mb: 3 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 2, 
              bgcolor: 'background.default',
              border: '1px solid',
              borderColor: 'divider',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" align="center">
              {title}
            </Typography>
            <Typography variant="h4" sx={{ mt: 1, color: '#FFCF44' }}>
              {formatPercentage(averageMargin)}
            </Typography>
          </Paper>
        </Box>
      ) : (
        <Box sx={{ mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" align="center">
            Margin: {formatPercentage(averageMargin)}
          </Typography>
        </Box>
      )}
      
      {/* Margin Percentage Chart */}
      <Paper 
        elevation={0}
        sx={{ 
          p: compact ? 1 : 2, 
          mb: compact ? 1 : 3,
          bgcolor: 'background.default',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {!compact && (
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Margin Percentage
          </Typography>
        )}
        
        {isLoading ? (
          <Typography variant="body2" color="text.secondary" align="center">
            Loading margin percentage data...
          </Typography>
        ) : error ? (
          <Typography variant="body2" color="error" align="center">
            {error}
          </Typography>
        ) : marginData.length > 0 ? (
          <Box sx={{ height: compact ? 240 : 300 }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" align="center">
            No margin percentage data available
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MarginPercentageChart;
